import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { productsAPI } from '../../services/api';
import { useToast } from '../../components/ui/Toast';
import { Package, CheckCircle, XCircle, AlertCircle, Search, Edit, Trash2, Plus, Eye } from 'lucide-react';
import { ProductFormModal } from '../../components/products/ProductFormModal';
import { ProductViewModal } from '../../components/products/ProductViewModal';
import { ProductDeleteModal } from '../../components/products/ProductDeleteModal';

interface Product {
  product_id: number;
  name: string;
  description?: string;
  price: string | number;
  status: 'Active' | 'Inactive' | 'Discontinued';
  created_at: string;
  updated_at: string;
  _count?: {
    batches: number;
    complaints: number;
  };
}

interface ProductFilters {
  status?: string;
  search?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

const PRODUCT_STATUS_LABELS = {
  Active: 'Active',
  Inactive: 'Inactive',
  Discontinued: 'Discontinued',
};

const ProductsPage: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [filters, setFilters] = useState<ProductFilters>({
    page: 1,
    limit: 10,
    sortBy: 'name',
    sortOrder: 'asc',
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  });
  const [productStats, setProductStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    discontinued: 0,
  });
  const { addToast } = useToast();

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await productsAPI.getAll(filters);
      setProducts(response.data.products);
      setPagination(response.data.pagination);
    } catch (error: any) {
      addToast({ type: 'error', title: 'Error', message: error.message || 'Failed to fetch products' });
    } finally {
      setLoading(false);
    }
  };

  const fetchProductStats = async () => {
    try {
      const statsParams = { page: 1, limit: 1000 };
      const response = await productsAPI.getAll(statsParams);
      
      if (response.success) {
        const allProducts = response.data.products || [];
        const stats = {
          total: response.data.pagination.total,
          active: allProducts.filter((p: Product) => p.status === 'Active').length,
          inactive: allProducts.filter((p: Product) => p.status === 'Inactive').length,
          discontinued: allProducts.filter((p: Product) => p.status === 'Discontinued').length,
        };
        setProductStats(stats);
      }
    } catch (error) {
      console.error('Failed to fetch product stats:', error);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, [filters]);

  useEffect(() => {
    fetchProductStats();
  }, []);

  const handleCreate = () => {
    setSelectedProduct(null);
    setFormModalOpen(true);
  };

  const handleEdit = (product: Product) => {
    setSelectedProduct(product);
    setFormModalOpen(true);
  };

  const handleView = (product: Product) => {
    setSelectedProduct(product);
    setViewModalOpen(true);
  };

  const handleDelete = (product: Product) => {
    setSelectedProduct(product);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedProduct) return;

    try {
      setDeleteLoading(true);
      await productsAPI.delete(selectedProduct.product_id);
      addToast({ type: 'success', title: 'Product deleted successfully' });
      setDeleteModalOpen(false);
      setSelectedProduct(null);
      fetchProducts();
      fetchProductStats();
    } catch (error: any) {
      addToast({ type: 'error', title: 'Error', message: error.message || 'Failed to delete product' });
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleFormSuccess = () => {
    fetchProducts();
    fetchProductStats();
  };

  const handleSearch = (value: string) => {
    setFilters(prev => ({ ...prev, search: value, page: 1 }));
  };

  const handleStatusFilter = (value: string) => {
    setFilters(prev => ({ ...prev, status: value === 'all' ? undefined : value, page: 1 }));
  };

  const handleStatusCardClick = (status: string) => {
    setFilters(prev => ({ ...prev, status: status === 'all' ? undefined : status, page: 1 }));
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const getStatusBadge = (status: string) => {
    const colors = {
      Active: 'bg-green-100 text-green-800',
      Inactive: 'bg-yellow-100 text-yellow-800',
      Discontinued: 'bg-red-100 text-red-800',
    };
    return colors[status as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      {/* Product Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card 
          className="bg-blue-50 dark:bg-blue-900/20 border-0 shadow-lg dark:border dark:border-gray-700 cursor-pointer hover:shadow-xl transition-shadow duration-200"
          onClick={() => handleStatusCardClick('all')}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Total Products</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white">{productStats.total}</p>
              </div>
              <div className="bg-blue-500 p-3 rounded-full">
                <Package className="h-6 w-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card 
          className="bg-green-50 dark:bg-green-900/20 border-0 shadow-lg dark:border dark:border-gray-700 cursor-pointer hover:shadow-xl transition-shadow duration-200"
          onClick={() => handleStatusCardClick('Active')}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Active</p>
                <p className="text-3xl font-bold text-green-600 dark:text-green-400">{productStats.active}</p>
              </div>
              <div className="bg-green-500 p-3 rounded-full">
                <CheckCircle className="h-6 w-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card 
          className="bg-yellow-50 dark:bg-yellow-900/20 border-0 shadow-lg dark:border dark:border-gray-700 cursor-pointer hover:shadow-xl transition-shadow duration-200"
          onClick={() => handleStatusCardClick('Inactive')}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Inactive</p>
                <p className="text-3xl font-bold text-yellow-600 dark:text-yellow-400">{productStats.inactive}</p>
              </div>
              <div className="bg-yellow-500 p-3 rounded-full">
                <AlertCircle className="h-6 w-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card 
          className="bg-red-50 dark:bg-red-900/20 border-0 shadow-lg dark:border dark:border-gray-700 cursor-pointer hover:shadow-xl transition-shadow duration-200"
          onClick={() => handleStatusCardClick('Discontinued')}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Discontinued</p>
                <p className="text-3xl font-bold text-red-600 dark:text-red-400">{productStats.discontinued}</p>
              </div>
              <div className="bg-red-500 p-3 rounded-full">
                <XCircle className="h-6 w-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-white dark:bg-gray-800 shadow-lg border-0 rounded-2xl dark:border dark:border-gray-700">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-2xl font-semibold text-gray-900 dark:text-white">Products Management</CardTitle>
            <Button onClick={handleCreate} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="h-4 w-4 mr-2" />
              Create Product
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search products..."
                value={filters.search || ''}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-10 h-12 border-gray-200 focus:border-blue-500 focus:ring-blue-500 rounded-xl"
              />
            </div>
            <Select value={filters.status || 'all'} onValueChange={handleStatusFilter}>
              <SelectTrigger className="w-full md:w-48 h-12 border-gray-200 focus:border-blue-500 focus:ring-blue-500 rounded-xl">
                <SelectValue placeholder="Filter by Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {Object.entries(PRODUCT_STATUS_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Table */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <>
              <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50 dark:bg-gray-700">
                      <TableHead className="font-semibold text-gray-900 dark:text-white">Name</TableHead>
                      <TableHead className="font-semibold text-gray-900 dark:text-white">Price</TableHead>
                      <TableHead className="font-semibold text-gray-900 dark:text-white">Status</TableHead>
                      <TableHead className="font-semibold text-gray-900 dark:text-white">Batches</TableHead>
                      <TableHead className="font-semibold text-gray-900 dark:text-white">Complaints</TableHead>
                      <TableHead className="font-semibold text-gray-900 dark:text-white">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {products.map((product) => (
                      <TableRow key={product.product_id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <TableCell>
                          <div>
                            <div className="font-medium text-gray-900 dark:text-white">{product.name}</div>
                            {product.description && (
                              <div className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs">
                                {product.description}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="font-medium text-gray-900 dark:text-white">
                          ${parseFloat(product.price.toString()).toFixed(2)}
                        </TableCell>
                        <TableCell>
                          <Badge className={getStatusBadge(product.status)}>
                            {product.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-medium text-gray-900 dark:text-white">
                          {product._count?.batches || 0}
                        </TableCell>
                        <TableCell className="font-medium text-gray-900 dark:text-white">
                          {product._count?.complaints || 0}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleView(product)}
                              className="h-8 w-8 p-0 hover:bg-blue-50"
                            >
                              <Eye className="h-4 w-4 text-blue-600" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(product)}
                              className="h-8 w-8 p-0 hover:bg-green-50"
                            >
                              <Edit className="h-4 w-4 text-green-600" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(product)}
                              className="h-8 w-8 p-0 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4 text-red-600" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {pagination.pages > 1 && (
                <div className="flex items-center justify-between mt-6">
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} results
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page === 1}
                      className="rounded-lg"
                    >
                      Previous
                    </Button>
                    {Array.from({ length: Math.min(pagination.pages, 5) }, (_, i) => {
                      const page = i + 1;
                      return (
                        <Button
                          key={page}
                          variant={page === pagination.page ? "default" : "outline"}
                          size="sm"
                          onClick={() => handlePageChange(page)}
                          className="rounded-lg"
                        >
                          {page}
                        </Button>
                      );
                    })}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page === pagination.pages}
                      className="rounded-lg"
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Modals */}
      <ProductFormModal
        product={selectedProduct}
        open={formModalOpen}
        onClose={() => {
          setFormModalOpen(false);
          setSelectedProduct(null);
        }}
        onSuccess={handleFormSuccess}
      />

      <ProductViewModal
        product={selectedProduct}
        open={viewModalOpen}
        onClose={() => {
          setViewModalOpen(false);
          setSelectedProduct(null);
        }}
      />

      <ProductDeleteModal
        open={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setSelectedProduct(null);
        }}
        onConfirm={handleDeleteConfirm}
        productName={selectedProduct?.name || ''}
        loading={deleteLoading}
      />
    </div>
  );
};

export default ProductsPage;